import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useExamStatus } from "../ExamStatusContext";
import { useScoreTable } from "../ScoreContext";
import { useToken } from "../../TokenContext";
import withResetOnNavigation from '../withResetOnNavigation';
import questions from "../QA/Test11_q_a";
import TestContainer from "../TestContainer";
import ZoomControls from "../ZoomControls";
import "../../../css/test11.css";
import "../../../css/examplequestion.css";

const Test11 = () => {
  const { examStatus } = useExamStatus();
  const { loggedInUser } = useToken();
  const navigate = useNavigate();

  const [inputValues, setInputValues] = useState({});
  const [isTest11Started, setIsTest11Started] = useState(false);
  const [notification, setNotification] = useState("");
  const [isTimeUp, setIsTimeUp] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const score11 = useRef(0);
  const inputRefs = useRef({});
  const scoreTable = useScoreTable();

  const testDuration = 180;

  const unitMapping = {
    1: "円", 2: "人", 3: "人", 4: "円", 5: "時間", 6: ["時", "分"],
    7: "ページ", 8: "時間", 10: "通り", 11: "リットル", 13: "ページ",
    14: "倍", 15: "人", 16: "円", 17: "個", 18: "cm", 20: "点",
  };

  const exampleQuestions = [
    { question: "1個60円の品物を5個買うといくらになりますか。", answer: 300, unit: unitMapping[1] },
    { question: "今11時52分です。今から30分前は何時何分でしたか。", answer: [11, 22], unit: unitMapping[6] },
  ];

  useEffect(() => {
    const test11Status = localStorage.getItem("test11Status");
    if (examStatus === "検査中" && test11Status === "started") {
      localStorage.setItem("test11Status", "notStarted");
    }
  }, [examStatus]);

  const handleNext = async () => {
    setIsLoading(true);
    try {
      const userName = localStorage.getItem("userName") || "anonymous";
      const scores = {
        G: parseInt(localStorage.getItem("G_score"), 10) || 0,
        V: parseInt(localStorage.getItem("V_score"), 10) || 0,
        N: parseInt(localStorage.getItem("N_score"), 10) || 0,
        Q: parseInt(localStorage.getItem("Q_score"), 10) || 0,
        S: parseInt(localStorage.getItem("S_score"), 10) || 0,
        P: parseInt(localStorage.getItem("P_score"), 10) || 0,
        K: parseInt(localStorage.getItem("K_score"), 10) || 0,
        F: parseInt(localStorage.getItem("F_score"), 10) || 0,
        M: parseInt(localStorage.getItem("M_score"), 10) || 0,
      };

      const grade = localStorage.getItem("grade") || "中学生";

      const response = await fetch("https://mpzzlvohx7.execute-api.ap-northeast-3.amazonaws.com/prod/api/GATB", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: userName, scores, grade, login_code: localStorage.getItem("loggedInToken")})
      });

      if (!response.ok) throw new Error("結果取得に失敗しました");

      const result = await response.json();
      navigate("/GATBresult", { state: result });
    } catch (error) {
      console.error("エラー:", error);
      alert("結果を取得できませんでした。もう一度お試しください。");
    } finally {
      setIsLoading(false);
    }
  };

  const gradeTest = useCallback(() => {
    let calculatedScore = 0;

    questions.forEach((question, index) => {
      const userAnswer = Array.isArray(inputValues[index])
        ? inputValues[index].map(val => typeof val === "string" ? val.toLowerCase() : val)
        : typeof inputValues[index] === "string"
          ? inputValues[index].toLowerCase()
          : inputValues[index];

      const correctAnswer = Array.isArray(question.answer)
        ? question.answer.map(val => typeof val === "string" ? val.toLowerCase() : val)
        : typeof question.answer === "string"
          ? question.answer.toLowerCase()
          : question.answer;

      if (Array.isArray(userAnswer) && Array.isArray(correctAnswer)) {
        if (JSON.stringify(userAnswer) === JSON.stringify(correctAnswer)) {
          calculatedScore += 1;
        }
      } else if (userAnswer === correctAnswer) {
        calculatedScore += 1;
      }
    });

    score11.current = calculatedScore;
    localStorage.setItem("score11", calculatedScore);

    const [gScore, nScore] = scoreTable.scoreTable11[String(calculatedScore)] || [0, 0];
    localStorage.setItem("G_score", (parseInt(localStorage.getItem("G_score"), 10) || 0) + gScore);
    localStorage.setItem("N_score", (parseInt(localStorage.getItem("N_score"), 10) || 0) + nScore);

    setNotification("検査を終了します！");
    setIsTimeUp(true);
  }, [inputValues, scoreTable.scoreTable11]);

  const handleStartTest = () => {
    setIsTest11Started(true);
    localStorage.setItem("test11Status", "started");
  };

  const handleInputChange = (e, index, subIndex) => {
    const value = e.target.value.replace(/[^a-zA-Z0-9]/g, "");

    setInputValues(prev => {
      const updated = { ...prev };

      if (Array.isArray(questions[index].answer)) {
        if (!Array.isArray(updated[index])) updated[index] = [];
        const arrayCopy = [...updated[index]];
        arrayCopy[subIndex] = value;
        updated[index] = arrayCopy;
      } else {
        updated[index] = value;
      }

      return updated;
    });

    if (
      value.length >= 1 &&
      index === currentQuestionIndex &&
      (!subIndex || subIndex === questions[index].answer?.length - 1)
    ) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handleKeyDown = (e, index, subIndex) => {
    if (e.key === "Enter") {
      if (Array.isArray(questions[index].answer) &&
        inputRefs.current[`${index}-${subIndex + 1}`]) {
        inputRefs.current[`${index}-${subIndex + 1}`].focus();
      } else if (inputRefs.current[`${index + 1}-0`]) {
        inputRefs.current[`${index + 1}-0`].focus();
      }
    }
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <h2 className="loading-message">結果を生成中です...</h2>
        <p>しばらくお待ちください。</p>
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!isTest11Started) {
    return (
      <div className="test11-container">
        <h1>検査9 - やり方の説明</h1>
        <p>
          各問題、問題文をよく読んで解答を入力してください。
          <br />
          解答は半角英数字で入力してください。
        </p>
        <h2>例題</h2>
        <div className="example-container">
          <div className="example-questions">
            {exampleQuestions.map((example, index) => (
              <div key={index} className="test11-question">
                <span className="test11-word">{example.question}</span>
                <div className="horizontal-inputs">
                  {Array.isArray(example.unit)
                    ? example.answer.map((value, subIndex) => (
                      <div key={subIndex} className="input-with-label">
                        <input type="text" value={value.toString()} readOnly className="test11-input example half-width" />
                        <span className="unit-label">{example.unit[subIndex]}</span>
                      </div>
                    ))
                    : (
                      <div className="input-with-label">
                        <input type="text" value={example.answer.toString()} readOnly className="test11-input example" />
                        <span className="unit-label">{example.unit}</span>
                      </div>
                    )}
                </div>
              </div>
            ))}
          </div>
        </div>
        <button className="start-button" onClick={handleStartTest}>検査に進む</button>
      </div>
    );
  }

  const renderedQuestions = questions.slice(0, currentQuestionIndex + 1).map((question, index) => (
    <div key={question.id} className="test11-question">
      <p>問題 {index + 1}</p>
      <span className="test11-word">{question.question}</span>
      {Array.isArray(question.answer) ? (
        <div className="horizontal-inputs">
          {question.answer.map((_, subIndex) => (
            <div key={subIndex} className="input-with-label">
              <input
                ref={(el) => inputRefs.current[`${index}-${subIndex}`] = el}
                type="text"
                value={inputValues[index]?.[subIndex] || ""}
                onChange={(e) => handleInputChange(e, index, subIndex)}
                onKeyDown={(e) => handleKeyDown(e, index, subIndex)}
                className="test11-input half-width"
              />
              <span className="unit-label">{unitMapping[question.id]?.[subIndex] || ""}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="input-with-label">
          <input
            ref={(el) => inputRefs.current[`${index}-0`] = el}
            type="text"
            value={inputValues[index] || ""}
            onChange={(e) => handleInputChange(e, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            className="test11-input"
          />
          <span className="unit-label">{unitMapping[question.id]}</span>
        </div>
      )}
    </div>
  ));

  return (
    <ZoomControls>
      <TestContainer
        title="検査9"
        isTimeUp={isTimeUp}
        notification={notification}
        renderedQuestions={renderedQuestions}
        manageTimer={true}
        gradeTest={gradeTest}
        testDuration={testDuration}
        handleNextTest={handleNext}
      />
    </ZoomControls>
  );
};

export default withResetOnNavigation(Test11);
